/**
 * PhotoViewer — listing photo gallery for the PDP, ported from od-prototypes photo-viewer v2.
 *
 * Layout
 * - Fills its container's width and restores the desktop gallery's 8:3 frame. The hero
 *   (photo 0) stays a true 3:2 while the four supporting tiles share the remaining width.
 * - Later columns are sized from the stage height: wide photos get a full-height column
 *   (aspect 1.5), others stack two square tiles per column, with a 4px gap.
 *
 * Behaviour
 * - The stage never scrolls natively. It moves via a horizontal swipe over the photos or
 *   ← / →, always settling on a column boundary. Phones show every listing photo with the
 *   iOS-style page indicator and support long-press horizontal scrubbing.
 * - Counter (top-left) and a share / heart pill (top-right) sit 12px inside the photo area.
 * - Zoom: click a photo (or pinch out) and it expands to fill the window; every photo is
 *   then stacked vertically, scroll-snapped one at a time. Click, pinch in, or Esc closes.
 *   The zoom layer is portalled to <body>.
 * - prefers-reduced-motion: every animation becomes a cut.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { ReactNode } from 'react';

import { Button } from '@opendoor/atoms/button';
import { IconLibraryPhoto, IconShare2, IconX } from '@tabler/icons-react';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { buildGalleryPhotos } from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import {
  buildWorkerPhotoUrl,
  getLightboxThumbnailUrl,
  pickFirstPresizedUrl,
} from 'components/marketplace/helpers/presizedPhotos';
import { GalleryMediaActions } from 'components/marketplace/property/media/GalleryMediaActions';
import { FavoriteHeartIcon } from 'components/marketplace/shared/FavoriteHeartIcon';
import { PropertyPhotoPlaceholder } from 'components/marketplace/shared/PropertyPhoto';
import {
  streetViewAltText,
  streetViewLocationFor,
  StreetViewPhoto,
} from 'components/marketplace/shared/StreetViewPhoto';
import { useTouring } from 'components/self-tour/hooks/useTouring';
import { animate } from 'framer-motion';
import { toImagesWorkerSourceUrl } from 'helpers/imageUrl';
import { createPortal } from 'react-dom';

import { BuyDirectContextModal } from '../PropertySection/BuyDirectContextModal';
import { SelfTourDialog } from '../PropertySection/SelfTourDialog';
import { TourWithAgentModal } from '../PropertySection/TourWithAgentModal';
import type { PropertyDetailsType } from '../types';

export interface ViewerPhoto {
  /** xl URL for the zoom morph and stack. The grid uses `srcSet` of the same 3:2 crop. */
  src: string;
  /** Responsive candidates for the grid and the server-rendered hero. */
  srcSet?: string;
  /** Small render for thumbnail strips. */
  thumb: string;
  alt: string;
  /** true → full-height column; false → stacked two per column. */
  wide: boolean;
}

/* ---- locked design values ---------------------------------------------------- */
const CONFIG = {
  grid: {
    padding: 0,
    gap: 4,
    frameRatio: 8 / 3,
    heroRatio: 3 / 2,
    topRadius: 14,
  },
  columnsAfter: {
    stackedAspect: 1,
    wideAspect: 1.5,
  },
  zoom: {
    open: { duration: 0.45, ease: [0.65, 0, 0.35, 1] as [number, number, number, number] },
    close: { duration: 0.35, ease: [0.65, 0, 0.35, 1] as [number, number, number, number] },
    sideInset: 80,
    stackGap: 4,
    snap: true,
    backdrop: 'var(--color-od-bg-scrim)',
  },
};
type ViewerConfig = typeof CONFIG;
const INK = 'var(--color-od-content-primary)';
const CANVAS = 'var(--color-od-bg-secondary)';
/** Height of the site header the zoom bar replaces while a photo is open. */
const BAR_H = 72;
const ZOOM_SCRUBBER_SPACE = 112;
/** Zoomed photos come from the 3:2 image preset. */
const ZOOM_PHOTO_RATIO = 3 / 2;

/** Below this width the viewer behaves like a phone: one photo at a time. */
const PHONE_MAX_WIDTH = 768;
const isPhone = (W: number) => W < PHONE_MAX_WIDTH;
const MOBILE_SCRUB_HOLD_MS = 500;
const MOBILE_SCRUB_DRAG_MULTIPLIER = 1.5;

export const mobilePhotoScrubIndex = ({
  count,
  currentX,
  startIndex,
  startX,
  stripWidth,
}: {
  count: number;
  currentX: number;
  startIndex: number;
  startX: number;
  stripWidth: number;
}) => {
  if (count <= 0 || stripWidth <= 0) return 0;
  const slotWidth = stripWidth / count;
  const indexDelta = Math.round(((currentX - startX) * MOBILE_SCRUB_DRAG_MULTIPLIER) / slotWidth);
  return Math.max(0, Math.min(count - 1, startIndex + indexDelta));
};

function geometry(W: number, g: ViewerConfig['grid']) {
  if (isPhone(W)) {
    const heroW = W - 2 * g.padding;
    const heroH = heroW / g.heroRatio;
    return { heroW, heroH, frameH: heroH + 2 * g.padding };
  }
  const heroH = (W - 2 * g.padding) / g.frameRatio;
  const heroW = heroH * g.heroRatio;
  return { heroW, heroH, frameH: heroH + 2 * g.padding };
}

const CSS = `
  .pv-root{
    --bg: var(--color-od-bg-primary);
    --gap: 4px;
    --pill-h: 46px;
    --hero-w: 56.25%;
    --hero-h: 100%;
    --od-canvas: ${CANVAS};
    position:relative; width:100%;
    padding:var(--pad, 16px);
    background:var(--bg);
    overflow:hidden;
    -webkit-font-smoothing:antialiased;
    /* Before the engine measures, hold the original desktop gallery frame. */
    aspect-ratio: 8 / 3;
  }
  /* The server-rendered hero: exactly where the hero cell goes, so the grid replaces it seamlessly. */
  .pv-root .ssr-hero{
    position:absolute; left:0; top:0; height:100%; width:56.25%;
    object-fit:cover; display:block; border-radius:var(--stage-radius, 14px) 0 0 var(--stage-radius, 14px);
  }
  .pv-root .ssr-hero[hidden]{ display:none; }
  .pv-root.empty .empty-hero{ position:absolute; inset:0; }
  .pv-root.empty .empty-hero > *{ height:100%; width:100%; }
  .pv-root.empty .scrubber,
  .pv-root.empty .mobile-page-indicator,
  .pv-root.empty .counter,
  .pv-root.empty .actions{ display:none; }
  @media (max-width: ${PHONE_MAX_WIDTH - 1}px){
    .pv-root{ aspect-ratio: 3 / 2; --hero-w: 100%; }
    .pv-root .ssr-hero{ width:100%; border-radius:var(--stage-radius, 14px); }
  }
  .pv-root, .pv-root *{ box-sizing:border-box; }
  .pv-root .viewer{ position:absolute; inset:var(--pad, 16px); display:flex; flex-direction:column; }
  .pv-root .stage{
    flex:1 1 auto; position:relative; overflow:hidden; touch-action:pan-y;
    border-radius:var(--stage-radius, 14px);
  }
  .pv-root .track{ display:flex; gap:var(--gap); height:100%; align-items:stretch; width:max-content; }
  .pv-root .col{ flex:0 0 auto; height:100%; display:flex; flex-direction:column; gap:var(--gap); }
  .pv-root .cell{ position:relative; flex:1 1 0; overflow:hidden; border-radius:4px; background:var(--color-od-bg-tertiary); cursor:pointer; }
  .pv-root .col:first-child .cell{ border-radius:14px 4px 4px 14px; }
  .pv-root .col:nth-child(3) .cell:first-child{ border-top-right-radius:14px; }
  .pv-root .col:nth-child(3) .cell:last-child{ border-bottom-right-radius:14px; }
  .pv-root .col:only-child .cell{ border-radius:14px; }
  .pv-root .cell img{ width:100%; height:100%; object-fit:cover; display:block; user-select:none; -webkit-user-drag:none; }
  .pv-root .stage img{ -webkit-touch-callout:none; }
  .pv-root .hero-overlay{
    position:absolute; left:0; top:0; width:var(--hero-w); height:var(--hero-h);
    z-index:25; pointer-events:none;
  }
  .pv-root .hero-overlay > *{ pointer-events:none; }
  .pv-root .tour-3d{
    position:absolute; left:0; top:0; width:var(--hero-w); height:var(--hero-h);
    z-index:26; pointer-events:none;
  }
  .pv-root .gallery-media-actions{
    position:absolute; right:10px; top:10px; z-index:26;
    display:flex; align-items:center; gap:0; pointer-events:auto;
    border-radius:999px;
  }
  .pv-root .gallery-media-actions[data-grouped='true']{
    padding:1px;
    border:1px solid color-mix(in srgb, var(--color-od-content-primary) 15%, transparent);
    background:color-mix(in srgb, var(--color-od-bg-primary) 72%, transparent);
    box-shadow:0 3px 12px color-mix(in srgb, var(--color-od-content-primary) 9%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-od-bg-primary) 42%, transparent);
    -webkit-backdrop-filter:blur(18px) saturate(160%);
    backdrop-filter:blur(18px) saturate(160%);
  }
  .pv-root .gallery-action-button{
    height:36px; padding:0 12px;
    display:flex; align-items:center; justify-content:center; gap:8px;
    border-radius:999px;
    border:1px solid color-mix(in srgb, var(--color-od-content-primary) 15%, transparent) !important;
    background:color-mix(in srgb, var(--color-od-bg-primary) 72%, transparent) !important;
    box-shadow:0 4px 14px color-mix(in srgb, var(--color-od-content-primary) 12%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-od-bg-primary) 55%, transparent) !important;
    -webkit-backdrop-filter:blur(18px) saturate(160%);
    backdrop-filter:blur(18px) saturate(160%);
    font-family:inherit; font-size:var(--text-label-sm);
    font-weight:var(--text-label-sm--font-weight); line-height:var(--text-label-sm--line-height);
    letter-spacing:var(--text-label-sm--letter-spacing);
    transition:background-color .16s ease;
  }
  .pv-root .gallery-media-action{
    background:color-mix(in srgb, var(--color-od-bg-primary) 48%, transparent) !important;
    box-shadow:0 3px 12px color-mix(in srgb, var(--color-od-content-primary) 9%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-od-bg-primary) 42%, transparent) !important;
    cursor:pointer;
    pointer-events:auto;
  }
  .pv-root .gallery-media-actions[data-grouped='true'] .gallery-media-action{
    width:36px; padding:0;
    border:0 !important; background:transparent !important; box-shadow:none !important;
    -webkit-backdrop-filter:none; backdrop-filter:none;
  }
  .pv-root .gallery-media-actions[data-grouped='true'] .gallery-media-label{ display:none; }
  .pv-root .show-all-photos{
    position:absolute; right:10px; bottom:10px; z-index:26;
    background:color-mix(in srgb, var(--color-od-bg-primary) 88%, transparent) !important;
    box-shadow:0 4px 16px color-mix(in srgb, var(--color-od-content-primary) 16%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-od-bg-primary) 78%, transparent) !important;
    cursor:pointer;
  }
  .pv-root .gallery-media-action:hover,
  .pv-root .gallery-media-action:focus-visible,
  .pv-root .show-all-photos:hover,
  .pv-root .show-all-photos:focus-visible,
  .pv-root .gallery-media-actions[data-grouped='true'] .gallery-media-action:hover,
  .pv-root .gallery-media-actions[data-grouped='true'] .gallery-media-action:focus-visible{
    background:color-mix(in srgb, var(--color-od-bg-primary) 96%, transparent) !important;
  }

  .pv-zoom{ position:fixed; inset:0; z-index:3000; display:none; --od-canvas: ${CANVAS}; -webkit-font-smoothing:antialiased; }
  .pv-zoom:focus,
  .pv-restored-focus:focus-visible{ outline:none !important; }
  .pv-zoom.on{ display:block; }
  /* Drop below body-portaled dialogs while share, tour, or buy is open. */
  .pv-zoom.yield{ z-index:0; }
  .pv-zoom *{ box-sizing:border-box; }
  .pv-zoom .zoom-backdrop{ position:absolute; inset:0; opacity:0; }
  .pv-zoom .zoom-img{ position:absolute; left:0; top:0; overflow:hidden; cursor:zoom-out; }
  .pv-zoom .zoom-img img{ width:100%; height:100%; object-fit:cover; display:block; -webkit-user-drag:none; }
  .pv-zoom .zoom-scroll{
    position:absolute; left:0; top:0; width:100%; overflow-y:auto; overflow-x:hidden;
    overscroll-behavior:contain;
    display:flex; flex-direction:column; gap:var(--zoom-gap, 4px);
    opacity:0; pointer-events:none;
  }
  .pv-zoom .zoom-scroll.snap{ scroll-snap-type:y mandatory; }
  .pv-zoom .zoom-scroll.live{ opacity:1; pointer-events:auto; }
  .pv-zoom .zoom-slot{
    flex:none; width:100%; height:var(--slot-h, 100%); display:flex; align-items:center; justify-content:center;
    scroll-snap-align:start; overflow:hidden; cursor:zoom-out;
  }
  .pv-zoom .zoom-frame{
    width:var(--photo-w, 100%); height:var(--photo-h, 100%); overflow:hidden; cursor:zoom-in;
  }
  .pv-zoom .zoom-frame img{
    width:100%; height:100%; object-fit:contain; display:block; -webkit-user-drag:none;
    transform-origin:0 0; transition:transform .08s linear;
  }
  .pv-zoom .zoom-frame.toggling img{ transition:transform .25s ease; }
  .pv-zoom .zoom-frame.magnified{ cursor:zoom-out; touch-action:none; }
  .pv-zoom .zoom-frame.panning img{ transition:none; }
  .pv-zoom .zoom-scrubber{
    position:absolute; left:50%; bottom:20px; z-index:45;
    width:min(calc(100% - 48px), 1280px); height:72px; padding:8px;
    display:flex; align-items:center; gap:8px; overflow-x:auto; overflow-y:hidden;
    transform:translateX(-50%); border-radius:14px;
    background:color-mix(in srgb, var(--color-od-bg-inverse-primary) 82%, transparent); backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
    opacity:0; scrollbar-width:none; overscroll-behavior-x:contain;
  }
  .pv-zoom .zoom-scrubber::-webkit-scrollbar{ display:none; }
  .pv-zoom .zoom-thumb{
    flex:0 0 76px; width:76px; height:56px; padding:3px; border:0; border-radius:8px;
    background:transparent; cursor:pointer;
  }
  .pv-zoom .zoom-thumb img{
    width:100%; height:100%; object-fit:cover; display:block; border-radius:5px; -webkit-user-drag:none;
  }
  .pv-zoom .zoom-thumb.active{ background:var(--color-od-bg-primary); }

  .pv-zoom .icon-btn{
    width:24px; height:24px; padding:0; border:none; background:none;
    display:grid; place-items:center; cursor:pointer; color:${INK};
  }
  .pv-zoom .icon-btn svg{ width:24px; height:24px; display:block; }
  .pv-root .counter, .pv-zoom .counter{
    position:absolute; top:12px; left:12px; z-index:30;
    height:40px; display:flex; align-items:center;
    background:var(--od-canvas); color:${INK}; font-size:var(--text-label-xs); letter-spacing:var(--text-label-xs--letter-spacing);
    font-weight:var(--text-label-xs--font-weight); line-height:var(--text-label-xs--line-height);
    padding:0 11px; border-radius:999px; font-variant-numeric:tabular-nums;
  }
  .pv-root .counter{ display:none; }

  .pv-root .scrubber{
    position:absolute; left:0; right:0; bottom:26px; z-index:20;
    display:none; align-items:center; justify-content:center; padding:0 24px; pointer-events:none;
  }
  .pv-root .strip-wrap{ position:relative; pointer-events:auto; max-width:100%; }
  .pv-root .pill{
    position:relative; height:var(--pill-h); border-radius:10px;
    background:color-mix(in srgb, var(--color-od-bg-inverse-primary) 72%, transparent); backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
    cursor:grab; touch-action:none;
  }
  .pv-root .pill.scrubbing{ cursor:grabbing; }
  .pv-root .pill::after{ content:''; position:absolute; left:-8px; right:-8px; top:-12px; bottom:-12px; }
  .pv-root .window{
    position:absolute; background:var(--color-od-bg-primary); border-radius:10px;
    box-shadow:0 2px 10px color-mix(in srgb, var(--color-od-bg-scrim) 22%, transparent); z-index:1; pointer-events:none;
  }
  .pv-root .clip{
    position:absolute; inset:0; border-radius:inherit; overflow:hidden;
    -webkit-mask-image:linear-gradient(to right, transparent 0, var(--color-od-bg-scrim) var(--fadeL, 0px), var(--color-od-bg-scrim) calc(100% - var(--fadeR, 0px)), transparent 100%);
    mask-image:linear-gradient(to right, transparent 0, var(--color-od-bg-scrim) var(--fadeL, 0px), var(--color-od-bg-scrim) calc(100% - var(--fadeR, 0px)), transparent 100%);
  }
  .pv-root .rail{ position:absolute; top:0; left:0; height:100%; will-change:transform; }
  .pv-root .thumbs{ position:absolute; inset:0; z-index:2; }
  .pv-root .thumb{ position:absolute; top:50%; left:0; border-radius:4px; overflow:hidden; background:var(--color-od-bg-inverse-secondary); transform-origin:left center; }
  .pv-root .thumb img{ width:100%; height:100%; object-fit:cover; display:block; -webkit-user-drag:none; }
  .pv-root .thumb:hover{ filter:brightness(1.18); }
  .pv-root .mobile-page-indicator{ display:none; }
  .pv-zoom .counter{ top:${BAR_H + 12}px; left:calc(var(--zoom-x, 80px) + 12px); opacity:0; }

  /* The bar that stands in for the site header while zoomed: same 72px height, new content. */
  .pv-zoom .zoom-bar{
    position:absolute; top:0; left:0; right:0; height:${BAR_H}px; z-index:40; opacity:0;
    display:flex; align-items:center; justify-content:space-between;
    padding:0 80px; background:var(--color-od-bg-primary); border-bottom:1px solid var(--color-od-border-primary);
  }
  .pv-zoom .zoom-bar-address{ display:flex; align-items:baseline; gap:8px; min-width:0; }
  .pv-zoom .zoom-bar-right{ display:flex; align-items:center; }
  .pv-zoom .zoom-bar-icons{ display:flex; align-items:center; gap:20px; margin-left:36px; }
  .pv-zoom .zoom-bar-address > span{ white-space:nowrap; }
  .pv-zoom .zoom-bar-address > span:first-child{ overflow:hidden; text-overflow:ellipsis; }
  .pv-root .stage.swiping .cell{ cursor:grabbing; }
  /* A lone photo (a floor plan) is shown whole, with no counter and no stack. */
  .pv-zoom.single .counter{ display:none; }
  .pv-zoom.single .zoom-scrubber{ display:none; }
  .pv-zoom.single .zoom-img img{ object-fit:contain; }
  @media (max-width: ${PHONE_MAX_WIDTH - 1}px){
    .pv-root .show-all-photos{ display:none; }
    .pv-root .gallery-media-action{ width:36px; padding:0; }
    .pv-root .gallery-media-label{ display:none; }
    .pv-root .stage.mobile-scrubbing{ touch-action:none; }
    .pv-root .mobile-page-indicator{
      position:absolute; left:50px; right:50px; bottom:12px; z-index:22;
      height:8px; display:flex; align-items:center; gap:var(--indicator-gap, 5px);
      pointer-events:none;
    }
    .pv-root .mobile-page-segment{
      flex:1 1 0; min-width:1px; height:3.5px; border-radius:2px;
      background:color-mix(in srgb, var(--color-od-content-inverse-primary) 65%, transparent);
      transition:height .15s ease-in-out, background-color .15s ease-in-out;
    }
    .pv-root .mobile-page-segment.active{ height:6px; background:var(--color-od-bg-primary); }
    .pv-root .mobile-page-indicator.scrubbing .mobile-page-segment.active{ height:8px; }
    .pv-root .mobile-page-indicator.scrubbing .mobile-page-segment.adjacent{ height:6px; }
    .pv-root .mobile-page-indicator.single{ display:none; }
    .pv-zoom .zoom-bar{ padding:0 16px; }
    .pv-zoom .zoom-bar-address{ flex-direction:column; align-items:flex-start; gap:0; }
    .pv-zoom .zoom-bar-actions{ display:none; }
    .pv-zoom .zoom-bar-icons{ gap:16px; margin-left:16px; }
    .pv-zoom .zoom-scrubber{
      bottom:12px; width:calc(100% - 24px); height:64px; padding:6px; gap:6px;
    }
    .pv-zoom .zoom-thumb{ flex-basis:64px; width:64px; height:52px; }
  }
`;

/* PAD is every gap in the scrubber; a boundary between inside and outside measures PAD + PAD. */
const PAD = 8;
const THUMB_ASPECT = 4 / 3;
const COVER_IN = 0.05;
const COVER_FULL = 0.8;
const COVER_MID = (COVER_IN + COVER_FULL) / 2;
const COVER_HYST = 0.05;
const SETTLE_MS = 160;
const LIVE_IN_MS = 130;
const SIDE_ROOM = 22 + 38 * 2 + 10 + 28;
const SCRUBBER_EDGE = 24;

function fallback(w: number, h: number) {
  return (
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"/>`)
  );
}
function makeImg(
  src: string,
  alt: string,
  w: number,
  h: number,
  srcSet?: string,
  sizes?: string,
  loading?: 'eager' | 'lazy',
) {
  const img = document.createElement('img');
  if (loading) img.loading = loading;
  img.src = src;
  if (srcSet) {
    img.srcset = srcSet;
    if (sizes) img.sizes = sizes;
  }
  img.alt = alt;
  img.draggable = false;
  img.addEventListener(
    'error',
    () => {
      img.removeAttribute('srcset');
      img.style.backgroundColor = 'var(--color-od-bg-tertiary)';
      img.src = fallback(w, h);
    },
    { once: true },
  );
  return img;
}
function smoothstep(e0: number, e1: number, x: number) {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
function el(scope: ParentNode, key: string): HTMLElement {
  const node = scope.querySelector(`[data-pv="${key}"]`);
  if (!(node instanceof HTMLElement)) throw new Error(`PhotoViewer: missing [data-pv="${key}"]`);
  return node;
}

type Range = [number, number];
type Rect = { x: number; y: number; w: number; h: number };
type DragState = {
  id: number;
  startX: number;
  startScroll: number;
  gain: number;
  slop: number;
  lastX: number;
  lastT: number;
  v: number;
  thumbIdx: number;
  moved: boolean;
};

/* All viewer behaviour. Runs once per mounted root; returns a teardown. */
const VIEWER_IMAGE_SIZES = '(max-width: 767px) 100vw, 50vw';

function isTextEntry(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}

function mountViewer(
  root: HTMLElement,
  zoom: HTMLElement,
  GRID_PHOTOS: ViewerPhoto[],
  ALL_PHOTOS: ViewerPhoto[],
  cfg: ViewerConfig,
) {
  const viewer = el(root, 'viewer');
  const stage = el(root, 'stage');
  const track = el(root, 'track');
  const pill = el(root, 'pill');
  const clip = el(root, 'clip');
  const rail = el(root, 'rail');
  const thumbsEl = el(root, 'thumbs');
  const winEl = el(root, 'window');
  const mobileIndicator = el(root, 'mobileIndicator');
  const counter = el(root, 'counter');
  const showAllButton = root.querySelector('[data-pv="showAll"]');
  const inert = !!root.closest('[inert]');

  const cells: { colIndex: number; el: HTMLElement }[] = [];
  const columns: { el: HTMLElement; photos: number[] }[] = [];
  const thumbEls: HTMLElement[] = [];
  const mobilePageSegments = Array.from(
    mobileIndicator.querySelectorAll<HTMLElement>('.mobile-page-segment'),
  );
  let tW = 40;
  let tH = 30;
  let maxActive = 1;
  let colGeom: { l: number; w: number }[] = [];
  let colIn: boolean[] = [];
  let stageIsPhone = isPhone(root.clientWidth);
  let stagePhotos = stageIsPhone ? ALL_PHOTOS : GRID_PHOTOS;

  function buildStage() {
    track.innerHTML = '';
    cells.length = 0;
    columns.length = 0;
    let i = 0;
    while (i < stagePhotos.length) {
      const col = document.createElement('div');
      col.className = 'col';
      const group: number[] = [];
      if (stageIsPhone || stagePhotos[i].wide || i === stagePhotos.length - 1) {
        group.push(i);
        i++;
      } else {
        group.push(i);
        i++;
        if (i < stagePhotos.length && !stagePhotos[i].wide) {
          group.push(i);
          i++;
        }
      }
      group.forEach((pi) => {
        const cell = document.createElement('div');
        cell.className = 'cell';
        cell.appendChild(
          makeImg(
            stagePhotos[pi].src,
            stagePhotos[pi].alt,
            1600,
            1067,
            stagePhotos[pi].srcSet,
            VIEWER_IMAGE_SIZES,
            stageIsPhone && pi > 0 ? 'lazy' : undefined,
          ),
        );
        col.appendChild(cell);
        cells[pi] = { colIndex: columns.length, el: cell };
      });
      columns.push({ el: col, photos: group });
      track.appendChild(col);
    }
    sizeColumns();
  }

  function sizeColumns() {
    const H = stage.clientHeight;
    const frame = geometry(root.clientWidth, cfg.grid);
    const initialSupportingWidth = (stage.clientWidth - frame.heroW - 2 * cfg.grid.gap) / 2;
    columns.forEach((col, ci) => {
      const n = col.photos.length;
      if (ci === 0 && n === 1) {
        col.el.style.width = frame.heroW + 'px';
        return;
      }
      if ((ci === 1 || ci === 2) && n === 2) {
        col.el.style.width = initialSupportingWidth + 'px';
        return;
      }
      const rowH = n === 1 ? H : (H - cfg.grid.gap) / 2;
      const aspect = n === 1 ? cfg.columnsAfter.wideAspect : cfg.columnsAfter.stackedAspect;
      col.el.style.width = Math.round(rowH * aspect) + 'px';
    });
    measureColumns();
  }

  function measureColumns() {
    colGeom = columns.map((c) => ({ l: c.el.offsetLeft, w: c.el.offsetWidth }));
    if (colIn.length !== colGeom.length) colIn = colGeom.map(() => false);
  }

  function buildThumbs() {
    thumbsEl.innerHTML = '';
    thumbEls.length = 0;
    stagePhotos.forEach((p) => {
      const t = document.createElement('div');
      t.className = 'thumb';
      t.appendChild(makeImg(p.thumb || p.src, p.alt, 120, 90, undefined, undefined, 'lazy'));
      thumbsEl.appendChild(t);
      thumbEls.push(t);
    });
    sizeThumbs();
  }

  function trackWidth() {
    const n = stagePhotos.length;
    return n * tW + (n + 3) * PAD;
  }
  let viewW = 0;

  function sizeThumbs() {
    const n = stagePhotos.length;
    if (n === 0) return;
    const sideRoom = isPhone(viewer.clientWidth) ? SCRUBBER_EDGE : SIDE_ROOM;
    const avail = Math.min(viewer.clientWidth - 48 - sideRoom * 2, 1180);
    maxActive = sweepExtremes().maxActive;
    const nRef = Math.min(n, 22);
    const w = (avail - (nRef + 3) * PAD) / nRef;
    tW = Math.max(28, Math.min(Math.round(w / 4) * 4, 44));
    tH = tW / THUMB_ASPECT;
    root.style.setProperty('--pill-h', tH + PAD * 2 + 'px');
    viewW = Math.min(trackWidth(), viewer.clientWidth - sideRoom * 2, 1240);
    pill.style.width = viewW + 'px';
    rail.style.width = trackWidth() + 'px';
  }

  function sizeMobileIndicator() {
    const count = stagePhotos.length;
    if (count <= 1) return;
    const availableWidth = Math.max(0, stage.clientWidth - 100);
    const ratioBasedSpacing = availableWidth / (count / 0.5 + count - 1);
    const densityAwareSpacing = Math.min(5, Math.max(2, ratioBasedSpacing));
    const maximumSpacingWithoutOverflow = Math.max(
      0,
      (availableWidth - count) / Math.max(1, count - 1),
    );
    mobileIndicator.style.setProperty(
      '--indicator-gap',
      Math.min(densityAwareSpacing, maximumSpacingWithoutOverflow) + 'px',
    );
  }

  function syncMobileIndicator(index: number) {
    mobilePageSegments.forEach((segment, segmentIndex) => {
      segment.classList.toggle('active', segmentIndex === index);
      segment.classList.toggle('adjacent', Math.abs(segmentIndex - index) === 1);
    });
  }

  function layoutThumbs(av: number[], disc: number[], range: Range) {
    const n = thumbEls.length;
    let x = PAD * (1 + av[n - 1]);
    const xs = Array.from({ length: n }, () => 0);
    for (let i = 0; i < n; i++) {
      if (i > 0) x += PAD + PAD * Math.abs(av[i] - av[i - 1]);
      const t = thumbEls[i];
      xs[i] = x;
      t.style.width = tW + 'px';
      t.style.height = tH + 'px';
      t.style.transform = 'translate(' + x.toFixed(2) + 'px, -50%)';
      t.style.opacity = (0.72 + 0.28 * av[i]).toFixed(3);
      t.classList.toggle('active', disc[i] === 1);
      x += tW;
    }
    const xAt = (f: number) => {
      f = Math.max(0, Math.min(n - 1, f));
      const i0 = Math.max(0, Math.min(n - 1, Math.floor(f)));
      const t = f - i0;
      const nxt = i0 + 1 < n ? xs[i0 + 1] : xs[i0] + tW + PAD;
      return xs[i0] + t * (nxt - xs[i0]);
    };
    const L = xAt(range[0]) - PAD;
    const R = xAt(range[1]) + tW + PAD;
    winEl.style.left = L.toFixed(2) + 'px';
    winEl.style.width = Math.max(0, R - L).toFixed(2) + 'px';
    winEl.style.height = tH + PAD * 2 + 'px';
    winEl.style.top = '0px';
    const maxOff = Math.max(0, trackWidth() - viewW);
    const off = Math.max(0, Math.min(maxOff, (L + R) / 2 - viewW / 2));
    rail.style.transform = 'translateX(' + (-off).toFixed(2) + 'px)';
    clip.style.setProperty('--fadeL', Math.min(24, off).toFixed(1) + 'px');
    clip.style.setProperty('--fadeR', Math.min(24, maxOff - off).toFixed(1) + 'px');
  }

  function coverAt(s: number) {
    const vw = stage.clientWidth;
    const out = Array.from({ length: colGeom.length }, () => 0);
    for (let i = 0; i < colGeom.length; i++) {
      const g = colGeom[i];
      const ov = Math.max(0, Math.min(s + vw, g.l + g.w) - Math.max(s, g.l));
      out[i] = ov / Math.min(g.w, vw);
    }
    return out;
  }
  function activationsAt(s: number) {
    const cov = coverAt(s);
    const out = Array.from({ length: stagePhotos.length }, () => 0);
    for (let ci = 0; ci < columns.length; ci++) {
      const a = smoothstep(COVER_IN, COVER_FULL, cov[ci]);
      for (const pi of columns[ci].photos) out[pi] = a;
    }
    return out;
  }
  function rangeAt(s: number): Range {
    if (columns.length === 0) return [0, 0];
    const cov = coverAt(s);
    let first = -1;
    let last = -1;
    let best = 0;
    for (let ci = 0; ci < cov.length; ci++) {
      if (cov[ci] > cov[best]) best = ci;
      if (cov[ci] >= COVER_MID) {
        for (const pi of columns[ci].photos) {
          if (first < 0) first = pi;
          last = pi;
        }
      }
    }
    if (first < 0) {
      const p = columns[best].photos;
      return [p[0], p[p.length - 1]];
    }
    return [first, last];
  }
  function visibleRange() {
    return rangeAt(stage.scrollLeft);
  }
  function sweepExtremes() {
    const max = maxScroll();
    let found = 1;
    const stride = Math.max(8, Math.round(max / 400));
    for (let s = 0; s <= max + stride; s += stride) {
      const [a, b] = rangeAt(Math.min(s, max));
      if (b - a + 1 > found) found = b - a + 1;
    }
    return { maxActive: found };
  }

  let liveMode = false;
  let liveT0 = 0;
  let entryAv: number[] | null = null;
  let entryRange: Range | null = null;
  let fromAv: number[] | null = null;
  let fromRange: Range | null = null;
  let rTo: Range | null = null;
  let blend = 1;
  let blendRaf: number | null = null;
  let lastAv: number[] | null = null;
  let lastRange: Range | null = null;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  function stopBlend() {
    if (blendRaf) {
      cancelAnimationFrame(blendRaf);
      blendRaf = null;
    }
  }
  const lerpAv = (a: number[], b: number[], t: number) => b.map((v, i) => a[i] + (v - a[i]) * t);
  const lerpRange = (a: Range, b: Range, t: number): Range => [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
  ];
  function avOfRange(r: Range) {
    const lo = r[0] - 0.5;
    const hi = r[1] + 0.5;
    const n = stagePhotos.length;
    const out = Array.from({ length: n }, () => 0);
    for (let i = 0; i < n; i++) out[i] = Math.max(0, Math.min(hi, i + 0.5) - Math.max(lo, i - 0.5));
    return out;
  }
  function rangeOfAv(av: number[]): Range {
    let sum = 0;
    let wsum = 0;
    for (let i = 0; i < av.length; i++) {
      sum += av[i];
      wsum += i * av[i];
    }
    if (sum <= 0) return [0, 0];
    const centre = wsum / sum;
    return [centre - (sum - 1) / 2, centre + (sum - 1) / 2];
  }
  function rangeFromColIn(): Range {
    let first = -1;
    let last = -1;
    for (let ci = 0; ci < columns.length; ci++) {
      if (!colIn[ci]) continue;
      for (const pi of columns[ci].photos) {
        if (first < 0) first = pi;
        last = pi;
      }
    }
    return first < 0 ? rangeAt(stage.scrollLeft) : [first, last];
  }
  function beginLive() {
    stopBlend();
    entryAv = lastAv ? lastAv.slice() : activationsAt(stage.scrollLeft);
    entryRange = lastRange ? [lastRange[0], lastRange[1]] : rangeAt(stage.scrollLeft);
    liveMode = true;
    liveT0 = reducedMotion.matches ? 0 : performance.now();
  }
  function endLive(dur = SETTLE_MS) {
    stopBlend();
    fromAv = lastAv ? lastAv.slice() : null;
    fromRange = lastRange ? [lastRange[0], lastRange[1]] : rangeAt(stage.scrollLeft);
    liveMode = false;
    rTo = rangeFromColIn();
    blend = 0;
    if (reducedMotion.matches) {
      blend = 1;
      fromAv = null;
      fromRange = null;
      syncScrubber();
      return;
    }
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      blend = 1 - Math.pow(1 - p, 3);
      syncScrubber();
      if (p < 1) blendRaf = requestAnimationFrame(tick);
      else {
        blendRaf = null;
        blend = 1;
        fromAv = null;
        fromRange = null;
        syncScrubber();
      }
    };
    blendRaf = requestAnimationFrame(tick);
  }

  function syncScrubber() {
    if (stagePhotos.length === 0) return;
    const cov = coverAt(stage.scrollLeft);
    const n = stagePhotos.length;
    let av: number[];
    let shown: number[];
    let range: Range;
    if (liveMode) {
      for (let ci = 0; ci < cov.length; ci++) {
        colIn[ci] = cov[ci] >= (colIn[ci] ? COVER_MID - COVER_HYST : COVER_MID + COVER_HYST);
      }
      if (!colIn.some(Boolean)) {
        let best = 0;
        for (let ci = 1; ci < cov.length; ci++) if (cov[ci] > cov[best]) best = ci;
        colIn[best] = true;
      }
      const cont = Array.from({ length: n }, () => 0);
      shown = Array.from({ length: n }, () => 0);
      columns.forEach((c, ci) => {
        const a = smoothstep(COVER_IN, COVER_FULL, cov[ci]);
        const d = colIn[ci] ? 1 : 0;
        for (const pi of c.photos) {
          cont[pi] = a;
          shown[pi] = d;
        }
      });
      const p = Math.min(1, (performance.now() - liveT0) / LIVE_IN_MS);
      const e = 1 - Math.pow(1 - p, 3);
      const contRange = rangeOfAv(cont);
      av = e >= 1 || !entryAv ? cont : lerpAv(entryAv, cont, e);
      range = e >= 1 || !entryRange ? contRange : lerpRange(entryRange, contRange, e);
    } else {
      const to = rTo || rangeAt(stage.scrollLeft);
      const target = avOfRange(to);
      av = !fromAv || blend >= 1 ? target : lerpAv(fromAv, target, blend);
      range = !fromRange || blend >= 1 ? to : lerpRange(fromRange, to, blend);
      shown = Array.from({ length: n }, () => 0);
      for (let i = Math.round(to[0]); i <= Math.round(to[1]); i++) shown[i] = 1;
      for (let ci = 0; ci < columns.length; ci++) colIn[ci] = shown[columns[ci].photos[0]] === 1;
    }
    layoutThumbs(av, shown, range);
    lastAv = av.slice();
    lastRange = [range[0], range[1]];
    let first = -1;
    let last = -1;
    for (let i = 0; i < n; i++)
      if (shown[i]) {
        if (first < 0) first = i;
        last = i;
      }
    counter.textContent =
      first === last ? `${first + 1} of ${n}` : `${first + 1}–${last + 1} of ${n}`;
    syncMobileIndicator(Math.max(0, first));
  }

  function maxScroll() {
    return Math.max(0, stage.scrollWidth - stage.clientWidth);
  }
  let anim: { raf: number } | null = null;
  let navTarget: number | null = null;
  function stopAnim() {
    if (anim) {
      cancelAnimationFrame(anim.raf);
      anim = null;
    }
  }
  function goTo(target: number, smooth = true, dur = 380) {
    stopAnim();
    target = Math.max(0, Math.min(target, maxScroll()));
    navTarget = target;
    if (!smooth || reducedMotion.matches) {
      stopBlend();
      liveMode = false;
      stage.scrollLeft = target;
      navTarget = null;
      fromAv = null;
      fromRange = null;
      rTo = rangeAt(target);
      blend = 1;
      syncScrubber();
      return;
    }
    const from = stage.scrollLeft;
    const dist = target - from;
    if (Math.abs(dist) < 1) {
      navTarget = null;
      endLive();
      return;
    }
    stopBlend();
    fromAv = lastAv ? lastAv.slice() : null;
    fromRange = lastRange ? [lastRange[0], lastRange[1]] : rangeAt(from);
    liveMode = false;
    rTo = rangeAt(target);
    blend = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      stage.scrollLeft = from + dist * e;
      blend = e;
      syncScrubber();
      if (p < 1 && anim) anim.raf = requestAnimationFrame(tick);
      else {
        anim = null;
        navTarget = null;
        blend = 1;
        fromAv = null;
        fromRange = null;
        syncScrubber();
      }
    };
    anim = { raf: requestAnimationFrame(tick) };
  }
  function scrollToPhoto(idx: number, smooth = true) {
    const cell = cells[idx];
    if (!cell) return;
    goTo(columns[cell.colIndex].el.offsetLeft, smooth);
  }
  function step(dir: number) {
    const cur = navTarget !== null ? navTarget : stage.scrollLeft;
    let target: number;
    if (dir > 0) {
      const nextCol = columns.find((c) => c.el.offsetLeft > cur + 2);
      target = nextCol ? nextCol.el.offsetLeft : maxScroll();
    } else {
      const prevCols = columns.filter((c) => c.el.offsetLeft < cur - 2);
      target = prevCols.length ? prevCols[prevCols.length - 1].el.offsetLeft : 0;
    }
    goTo(target);
  }

  function focusableZoomElements() {
    return Array.from(
      zoom.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => {
      if (element.tabIndex < 0 || element.closest('[hidden], [inert]')) return false;
      for (let node: HTMLElement | null = element; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        if (node === zoom) break;
      }
      return true;
    });
  }

  const onKey = (e: KeyboardEvent) => {
    if (zoom.classList.contains('yield')) return;
    const target = e.target;
    const inZoom = target instanceof Node && zoom.contains(target);
    const inViewer = target instanceof Node && root.contains(target);
    const atPage = target === document.body || target === document.documentElement;
    if (zoomed >= 0) {
      if (e.key === 'Tab') {
        const focusable = focusableZoomElements();
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (
          first &&
          last &&
          (!inZoom || target === zoom || (e.shiftKey ? target === first : target === last))
        ) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus({ preventScroll: true });
        }
        return;
      }
      if (!inZoom && !atPage) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeZoom();
      } else if (isTextEntry(target)) {
        return;
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        zoomStep(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        zoomStep(-1);
      }
      return;
    }
    if (isTextEntry(target)) return;
    if (stagePhotos.length === 0 || (!inViewer && !atPage)) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      step(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      step(-1);
    }
  };
  if (!inert) window.addEventListener('keydown', onKey);

  let drag: DragState | null = null;
  function scrubGain() {
    const nominalPlate = maxActive * tW + (maxActive + 1) * PAD;
    return maxScroll() / Math.max(1, trackWidth() - nominalPlate);
  }
  const onDown = (e: PointerEvent) => {
    e.preventDefault();
    if (anim) {
      stopAnim();
      navTarget = null;
      endLive();
    }
    const thumb = e.target instanceof Element ? e.target.closest('.thumb') : null;
    drag = {
      id: e.pointerId,
      startX: e.clientX,
      startScroll: stage.scrollLeft,
      gain: scrubGain(),
      slop: e.pointerType === 'touch' ? 10 : 4,
      lastX: e.clientX,
      lastT: performance.now(),
      v: 0,
      thumbIdx: thumb instanceof HTMLElement ? thumbEls.indexOf(thumb) : -1,
      moved: false,
    };
    pill.setPointerCapture(e.pointerId);
  };
  pill.addEventListener('pointerdown', onDown);
  const onMove = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    if (!drag.moved) {
      const dx = e.clientX - drag.startX;
      if (Math.abs(dx) < drag.slop) return;
      drag.moved = true;
      stopAnim();
      navTarget = null;
      drag.startX += Math.sign(dx) * drag.slop;
      beginLive();
      pill.classList.add('scrubbing');
      document.body.style.cursor = 'grabbing';
    }
    const now = performance.now();
    const dt = Math.max(1, now - drag.lastT);
    drag.v = 0.75 * drag.v + 0.25 * ((e.clientX - drag.lastX) / dt);
    drag.lastX = e.clientX;
    drag.lastT = now;
    const s = drag.startScroll + (e.clientX - drag.startX) * drag.gain;
    const c = Math.max(0, Math.min(maxScroll(), s));
    if (c !== s) {
      drag.startX = e.clientX;
      drag.startScroll = c;
    }
    stage.scrollLeft = c;
    syncScrubber();
  };
  pill.addEventListener('pointermove', onMove);
  function trackJump(clientX: number) {
    let best = 0;
    let bd = Infinity;
    thumbEls.forEach((t, i) => {
      const r = t.getBoundingClientRect();
      const d = Math.abs(clientX - (r.left + r.width / 2));
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    scrollToPhoto(best);
  }
  function settleSnap(d: { v: number; gain: number }) {
    const here = stage.scrollLeft;
    const vScroll = (d.v || 0) * d.gain;
    const bounds = columns.map((c) => Math.min(c.el.offsetLeft, maxScroll())).concat([maxScroll()]);
    let bL = 0;
    let bR = maxScroll();
    for (const b of bounds) {
      if (b <= here && b > bL) bL = b;
      if (b >= here && b < bR) bR = b;
    }
    const proj = here + vScroll * 150;
    const target = Math.abs(proj - bL) <= Math.abs(proj - bR) ? bL : bR;
    goTo(target, true, 240);
  }
  function endDrag(e?: PointerEvent) {
    if (!drag || (e && e.pointerId !== drag.id)) return;
    const d = drag;
    drag = null;
    pill.classList.remove('scrubbing');
    document.body.style.cursor = '';
    const isUp = !!e && e.type === 'pointerup';
    if (!d.moved) {
      if (isUp && d.thumbIdx >= 0) scrollToPhoto(d.thumbIdx);
      else if (isUp && e) trackJump(e.clientX);
      else endLive();
    } else {
      settleSnap(d);
    }
  }
  pill.addEventListener('pointerup', endDrag);
  pill.addEventListener('pointercancel', endDrag);

  /* ---------------- stage swipe ---------------- */
  // A finger swipe (or mouse drag) across the photos moves the grid 1:1 and snaps to the nearest
  // column on release. The stage allows vertical pans through, so the page still scrolls.
  type SwipeState = {
    id: number;
    startX: number;
    startScroll: number;
    lastX: number;
    lastT: number;
    v: number;
    slop: number;
    moved: boolean;
  };
  type MobileScrubState = {
    id: number;
    startX: number;
    startIndex: number;
    currentIndex: number;
    stripWidth: number;
  };
  let swipe: SwipeState | null = null;
  let mobileScrub: MobileScrubState | null = null;
  let mobileScrubTimer: ReturnType<typeof setTimeout> | undefined;
  let suppressCellClick = false;
  const clearMobileScrubTimer = () => {
    clearTimeout(mobileScrubTimer);
    mobileScrubTimer = undefined;
  };
  const setMobileScrubPresentation = (active: boolean) => {
    stage.classList.toggle('mobile-scrubbing', active);
    mobileIndicator.classList.toggle('scrubbing', active);
  };
  const beginMobileScrub = (pointerId: number, clientX: number) => {
    if (!stageIsPhone || stagePhotos.length <= 1 || !swipe || swipe.id !== pointerId) return;
    const startIndex = Math.max(0, Math.min(stagePhotos.length - 1, visibleRange()[0]));
    stopAnim();
    navTarget = null;
    swipe = null;
    mobileScrub = {
      id: pointerId,
      startX: clientX,
      startIndex,
      currentIndex: startIndex,
      stripWidth: stage.clientWidth,
    };
    setMobileScrubPresentation(true);
    if (typeof stage.setPointerCapture === 'function') stage.setPointerCapture(pointerId);
    navigator.vibrate?.(10);
  };
  const endMobileScrub = (pointerId?: number) => {
    if (!mobileScrub || (pointerId != null && mobileScrub.id !== pointerId)) return false;
    mobileScrub = null;
    setMobileScrubPresentation(false);
    suppressCellClick = true;
    setTimeout(() => {
      suppressCellClick = false;
    }, 0);
    return true;
  };
  const onStageDown = (e: PointerEvent) => {
    if (zoomed >= 0 || swipe || drag || mobileScrub) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (
      e.target instanceof Element &&
      e.target.closest('.hero-overlay, .gallery-media-actions, .show-all-photos')
    )
      return;
    swipe = {
      id: e.pointerId,
      startX: e.clientX,
      startScroll: stage.scrollLeft,
      lastX: e.clientX,
      lastT: performance.now(),
      v: 0,
      slop: e.pointerType === 'touch' ? 10 : 4,
      moved: false,
    };
    if (stageIsPhone && e.pointerType === 'touch' && stagePhotos.length > 1) {
      clearMobileScrubTimer();
      mobileScrubTimer = setTimeout(
        () => beginMobileScrub(e.pointerId, e.clientX),
        MOBILE_SCRUB_HOLD_MS,
      );
    }
  };
  const onStageMove = (e: PointerEvent) => {
    if (mobileScrub && e.pointerId === mobileScrub.id) {
      e.preventDefault();
      const nextIndex = mobilePhotoScrubIndex({
        count: stagePhotos.length,
        currentX: e.clientX,
        startIndex: mobileScrub.startIndex,
        startX: mobileScrub.startX,
        stripWidth: mobileScrub.stripWidth,
      });
      if (nextIndex === mobileScrub.currentIndex) return;
      mobileScrub.currentIndex = nextIndex;
      scrollToPhoto(nextIndex, false);
      navigator.vibrate?.(3);
      return;
    }
    if (!swipe || e.pointerId !== swipe.id) return;
    if (!swipe.moved) {
      const dx = e.clientX - swipe.startX;
      if (Math.abs(dx) < swipe.slop) return;
      clearMobileScrubTimer();
      swipe.moved = true;
      stopAnim();
      navTarget = null;
      beginLive();
      swipe.startX += Math.sign(dx) * swipe.slop;
      stage.setPointerCapture(e.pointerId);
      stage.classList.add('swiping');
    }
    const now = performance.now();
    const dt = Math.max(1, now - swipe.lastT);
    swipe.v = 0.75 * swipe.v + 0.25 * ((e.clientX - swipe.lastX) / dt);
    swipe.lastX = e.clientX;
    swipe.lastT = now;
    const s = swipe.startScroll - (e.clientX - swipe.startX);
    const c = Math.max(0, Math.min(maxScroll(), s));
    if (c !== s) {
      swipe.startX = e.clientX;
      swipe.startScroll = c;
    }
    stage.scrollLeft = c;
    syncScrubber();
  };
  const endSwipe = (e: PointerEvent) => {
    clearMobileScrubTimer();
    if (endMobileScrub(e.pointerId)) return;
    if (!swipe || e.pointerId !== swipe.id) return;
    const s = swipe;
    swipe = null;
    stage.classList.remove('swiping');
    if (!s.moved) return;
    // The click that follows a drag must not open the photo under the pointer.
    suppressCellClick = true;
    setTimeout(() => {
      suppressCellClick = false;
    }, 0);
    // Finger velocity runs opposite to scroll velocity.
    settleSnap({ v: s.v, gain: -1 });
  };
  stage.addEventListener('pointerdown', onStageDown);
  stage.addEventListener('pointermove', onStageMove);
  stage.addEventListener('pointerup', endSwipe);
  stage.addEventListener('pointercancel', endSwipe);
  const onStageTouchMove = (event: TouchEvent) => {
    if (mobileScrub) event.preventDefault();
  };
  stage.addEventListener('touchmove', onStageTouchMove, { passive: false });
  const onStageContextMenu = (event: MouseEvent) => {
    if (stageIsPhone) event.preventDefault();
  };
  stage.addEventListener('contextmenu', onStageContextMenu);

  /* ---------------- zoom ---------------- */
  const zoomBackdrop = el(zoom, 'zoomBackdrop');
  const zoomBox = el(zoom, 'zoomImg');
  const zoomScroll = el(zoom, 'zoomScroll');
  const zoomImgNode = zoomBox.querySelector('img');
  if (!zoomImgNode) throw new Error('PhotoViewer: zoom image missing');
  const zoomImg = zoomImgNode;
  zoomScroll.innerHTML = '';
  const zoomCounter = el(zoom, 'zoomCounter');
  const zoomBar = el(zoom, 'zoomBar');
  const zoomScrubber = el(zoom, 'zoomScrubber');
  zoomScrubber.innerHTML = '';
  const slots: HTMLElement[] = [];
  const zoomThumbButtons: HTMLButtonElement[] = [];
  let lightboxContentReady = false;
  function ensureLightboxContent() {
    if (lightboxContentReady) return;
    lightboxContentReady = true;
    ALL_PHOTOS.forEach((photo, index) => {
      const slot = document.createElement('div');
      slot.className = 'zoom-slot';
      const frame = document.createElement('div');
      frame.className = 'zoom-frame';
      const image = makeImg(photo.src, photo.alt, 1600, 1067, undefined, undefined, 'lazy');
      image.decoding = 'async';
      frame.appendChild(image);
      slot.appendChild(frame);
      zoomScroll.appendChild(slot);
      slots.push(slot);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'zoom-thumb';
      button.dataset.photoIndex = String(index);
      button.setAttribute('aria-label', `View photo ${index + 1}`);
      const thumbnail = makeImg(
        photo.thumb || photo.src,
        '',
        120,
        90,
        undefined,
        undefined,
        'lazy',
      );
      thumbnail.decoding = 'async';
      button.appendChild(thumbnail);
      zoomScrubber.appendChild(button);
      zoomThumbButtons.push(button);
    });
  }
  const zoomChrome = [zoomCounter, zoomBar, zoomScrubber];
  let zoomed = -1;
  let zoomAnim: ReturnType<typeof animate> | null = null;
  /** Set while a photo from outside the grid (a floor plan) is zoomed; it closes back to `origin`. */
  let single: { origin: HTMLElement; ratio: number } | null = null;
  let pageOverflow: string | null = null;
  let focusBeforeZoom: HTMLElement | null = null;
  // While a photo is open the page behind must not take wheel or scrollbar input; the zoom
  // stack scrolls instead and shows its own scrollbar in the same place.
  function lockPage() {
    if (pageOverflow !== null) return;
    pageOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
  function unlockPage() {
    if (pageOverflow === null) return;
    document.documentElement.style.overflow = pageOverflow;
    pageOverflow = null;
  }
  function focusZoom(trigger: HTMLElement | null) {
    focusBeforeZoom = trigger;
    zoom.focus({ preventScroll: true });
  }
  function restoreFocus() {
    const target = focusBeforeZoom;
    focusBeforeZoom = null;
    if (target?.isConnected) {
      const clearRestoredFocus = () => target.classList.remove('pv-restored-focus');
      target.classList.add('pv-restored-focus');
      target.addEventListener('blur', clearRestoredFocus, { once: true });
      target.focus({ preventScroll: true });
      if (document.activeElement === target) return;
      target.removeEventListener('blur', clearRestoredFocus);
      clearRestoredFocus();
    }
    const active = document.activeElement;
    if (active instanceof HTMLElement && zoom.contains(active)) active.blur();
  }

  function cellRect(idx: number): Rect {
    const b = cells[idx].el.getBoundingClientRect();
    return { x: b.left, y: b.top, w: b.width, h: b.height };
  }
  function fullRect(): Rect {
    // Full width minus the side insets, at the photo's own aspect ratio, starting right under
    // the nav bar. Taller-than-viewport photos are scrolled inside the stack instead of cropped.
    // Phones show zoomed photos edge to edge.
    const inset = isPhone(window.innerWidth) ? 0 : cfg.zoom.sideInset;
    // The stack's own scrollbar (if the browser draws one) takes room from the photo column.
    const scrollbar = zoomScroll.offsetWidth - zoomScroll.clientWidth;
    const availableWidth = window.innerWidth - scrollbar - 2 * inset;
    const availableHeight = slotHeight();
    const ratio = single ? single.ratio : ZOOM_PHOTO_RATIO;
    // Lightbox fit: the whole photo is visible, centred in the space under the bar.
    const w = Math.min(availableWidth, availableHeight * ratio);
    const h = w / ratio;
    return {
      x: (window.innerWidth - scrollbar - w) / 2,
      y: BAR_H + (availableHeight - h) / 2,
      w,
      h,
    };
  }
  /** The space under the bar; each photo in the stack gets exactly one screen of it. */
  function slotHeight() {
    return window.innerHeight - BAR_H - (single ? 0 : ZOOM_SCRUBBER_SPACE);
  }
  function setRect(r: Rect) {
    zoomBox.style.transform = `translate(${r.x.toFixed(2)}px, ${r.y.toFixed(2)}px)`;
    zoomBox.style.width = r.w.toFixed(2) + 'px';
    zoomBox.style.height = r.h.toFixed(2) + 'px';
  }
  function setChrome(o: number) {
    zoomBackdrop.style.opacity = o.toFixed(3);
    for (const node of zoomChrome) node.style.opacity = o.toFixed(3);
  }
  function slotPitch() {
    return slotHeight() + cfg.zoom.stackGap;
  }
  function layoutScroller() {
    // The scroller spans the window so its scrollbar sits at the right edge like the page's,
    // and only covers the visible area under the bar; each slot keeps the photo's height.
    // Size it first so fullRect() can see whether a scrollbar is being drawn.
    zoomScroll.style.transform = `translate(0px, ${BAR_H}px)`;
    zoomScroll.style.height = slotHeight() + 'px';
    const r = fullRect();
    zoom.style.setProperty('--slot-h', slotHeight() + 'px');
    zoom.style.setProperty('--photo-w', r.w + 'px');
    zoom.style.setProperty('--photo-h', r.h + 'px');
    zoom.style.setProperty('--zoom-gap', cfg.zoom.stackGap + 'px');
    zoomScroll.classList.toggle('snap', cfg.zoom.snap);
  }
  function scrollToSlot(idx: number, smooth: boolean) {
    zoomScroll.scrollTo({
      top: idx * slotPitch(),
      behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto',
    });
  }
  const onZoomScroll = () => {
    if (zoomed < 0 || !zoomScroll.classList.contains('live')) return;
    const idx = Math.max(
      0,
      Math.min(ALL_PHOTOS.length - 1, Math.round(zoomScroll.scrollTop / slotPitch())),
    );
    if (idx !== zoomed) {
      unmagnify(true);
      showZoomPhoto(idx);
    }
  };
  zoomScroll.addEventListener('scroll', onZoomScroll, { passive: true });

  function syncZoomScrubber(index: number) {
    const thumbnailHadFocus = zoomThumbButtons.some((button) => button === document.activeElement);
    zoomThumbButtons.forEach((button, buttonIndex) => {
      const active = buttonIndex === index;
      button.classList.toggle('active', active);
      button.tabIndex = active ? 0 : -1;
      if (active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    const active = zoomThumbButtons[index];
    if (thumbnailHadFocus && active !== document.activeElement) {
      active?.focus({ preventScroll: true });
    }
    if (!active || zoomScrubber.clientWidth === 0) return;
    const target = Math.max(
      0,
      Math.min(
        zoomScrubber.scrollWidth - zoomScrubber.clientWidth,
        active.offsetLeft - (zoomScrubber.clientWidth - active.offsetWidth) / 2,
      ),
    );
    zoomScrubber.scrollTo({
      left: target,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  }
  const onZoomScrubberClick = (event: MouseEvent) => {
    const button =
      event.target instanceof Element
        ? event.target.closest<HTMLButtonElement>('.zoom-thumb')
        : null;
    const index = Number(button?.dataset.photoIndex);
    if (!Number.isInteger(index) || index < 0 || index >= ALL_PHOTOS.length || single) return;
    unmagnify(true);
    showZoomPhoto(index);
    if (zoomScroll.classList.contains('live')) scrollToSlot(index, true);
  };
  zoomScrubber.addEventListener('click', onZoomScrubberClick);

  /* ---------------- magnify: click a lightbox photo to look closer inside its frame ---------------- */
  // The frame keeps its size; the photo scales up inside it and the visible part follows the
  // mouse (or a finger drag). Clicking again shows the whole photo.
  const MAGNIFY = 2.5;
  const TOGGLE_MS = 260;
  type Magnified = {
    frame: HTMLElement;
    img: HTMLImageElement;
    w: number;
    h: number;
    tx: number;
    ty: number;
  };
  let magnified: Magnified | null = null;
  let touchPan: {
    id: number;
    startX: number;
    startY: number;
    tx: number;
    ty: number;
    moved: boolean;
  } | null = null;
  // A finger drag that panned the photo must not count as the tap that toggles magnification.
  let panMoved = false;
  let toggleTimer: ReturnType<typeof setTimeout> | undefined;
  function frameOf(target: EventTarget | null) {
    const frame = target instanceof Element ? target.closest('.zoom-frame') : null;
    return frame instanceof HTMLElement ? frame : null;
  }
  function clampPan(m: Magnified, tx: number, ty: number) {
    return {
      tx: Math.min(0, Math.max(m.w - m.w * MAGNIFY, tx)),
      ty: Math.min(0, Math.max(m.h - m.h * MAGNIFY, ty)),
    };
  }
  function applyMagnify() {
    if (!magnified) return;
    magnified.img.style.transform = `translate(${magnified.tx.toFixed(2)}px, ${magnified.ty.toFixed(2)}px) scale(${MAGNIFY})`;
  }
  /** Show the part of the photo under the pointer: left edge of the frame ↔ left edge of the photo, etc. */
  function panToPointer(clientX: number, clientY: number) {
    if (!magnified) return;
    const b = magnified.frame.getBoundingClientRect();
    const fx = Math.max(0, Math.min(1, (clientX - b.left) / b.width));
    const fy = Math.max(0, Math.min(1, (clientY - b.top) / b.height));
    const c = clampPan(
      magnified,
      -fx * (magnified.w * MAGNIFY - magnified.w),
      -fy * (magnified.h * MAGNIFY - magnified.h),
    );
    magnified.tx = c.tx;
    magnified.ty = c.ty;
    applyMagnify();
  }
  function animateToggle(frame: HTMLElement) {
    clearTimeout(toggleTimer);
    frame.classList.add('toggling');
    toggleTimer = setTimeout(() => frame.classList.remove('toggling'), TOGGLE_MS);
  }
  function magnifyAt(frame: HTMLElement, clientX: number, clientY: number) {
    const img = frame.querySelector('img');
    if (!img) return;
    const b = frame.getBoundingClientRect();
    magnified = { frame, img, w: b.width, h: b.height, tx: 0, ty: 0 };
    frame.classList.add('magnified');
    animateToggle(frame);
    panToPointer(clientX, clientY);
  }
  function unmagnify(instant = false) {
    if (!magnified) return;
    const { img, frame } = magnified;
    magnified = null;
    touchPan = null;
    frame.classList.remove('magnified', 'panning');
    if (instant) {
      clearTimeout(toggleTimer);
      frame.classList.remove('toggling');
      img.style.transition = 'none';
      img.style.transform = '';
      void img.offsetWidth;
      img.style.transition = '';
    } else {
      animateToggle(frame);
      img.style.transform = '';
    }
  }
  const onMagnifyMove = (e: PointerEvent) => {
    if (!magnified) return;
    if (e.pointerType === 'mouse') {
      if (frameOf(e.target) === magnified.frame) panToPointer(e.clientX, e.clientY);
      return;
    }
    if (!touchPan || e.pointerId !== touchPan.id) return;
    const dx = e.clientX - touchPan.startX;
    const dy = e.clientY - touchPan.startY;
    if (!touchPan.moved) {
      if (Math.hypot(dx, dy) < 4) return;
      touchPan.moved = true;
      magnified.frame.classList.add('panning');
      magnified.img.setPointerCapture(e.pointerId);
    }
    const c = clampPan(magnified, touchPan.tx + dx, touchPan.ty + dy);
    magnified.tx = c.tx;
    magnified.ty = c.ty;
    applyMagnify();
  };
  const onMagnifyDown = (e: PointerEvent) => {
    if (!magnified || e.pointerType === 'mouse' || frameOf(e.target) !== magnified.frame) return;
    touchPan = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      tx: magnified.tx,
      ty: magnified.ty,
      moved: false,
    };
  };
  const endTouchPan = (e: PointerEvent) => {
    if (!touchPan || e.pointerId !== touchPan.id) return;
    const moved = touchPan.moved;
    touchPan = null;
    magnified?.frame.classList.remove('panning');
    if (moved) {
      panMoved = true;
      setTimeout(() => {
        panMoved = false;
      }, 0);
    }
  };
  zoomScroll.addEventListener('pointerdown', onMagnifyDown);
  zoomScroll.addEventListener('pointermove', onMagnifyMove);
  zoomScroll.addEventListener('pointerup', endTouchPan);
  zoomScroll.addEventListener('pointercancel', endTouchPan);
  const lerpRect = (a: Rect, b: Rect, t: number): Rect => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    w: a.w + (b.w - a.w) * t,
    h: a.h + (b.h - a.h) * t,
  });
  function applyZoomStyle() {
    zoom.style.setProperty('--zoom-x', fullRect().x + 'px');
    zoomBackdrop.style.background = cfg.zoom.backdrop;
  }
  function showZoomPhoto(idx: number) {
    zoomed = idx;
    zoomImg.removeAttribute('srcset');
    zoomImg.src = ALL_PHOTOS[idx].src;
    zoomImg.alt = ALL_PHOTOS[idx].alt;
    zoomCounter.textContent = `${idx + 1} of ${ALL_PHOTOS.length}`;
    syncZoomScrubber(idx);
  }
  function showStack(idx: number) {
    layoutScroller();
    zoomScroll.classList.remove('live');
    scrollToSlot(idx, false);
    zoomScroll.classList.add('live');
    zoomBox.style.visibility = 'hidden';
    syncZoomScrubber(idx);
  }
  function hideStack() {
    unmagnify(true);
    zoomScroll.classList.remove('live');
    zoomBox.style.visibility = '';
  }
  function openZoom(idx: number) {
    if (zoomed >= 0 || idx < 0) return;
    zoomAnim?.stop();
    ensureLightboxContent();
    applyZoomStyle();
    showZoomPhoto(idx);
    const from = cellRect(idx);
    hideStack();
    setRect(from);
    setChrome(0);
    zoom.classList.add('on');
    lockPage();
    focusZoom(showAllButton instanceof HTMLElement ? showAllButton : null);
    // Measure the target only once the layer is showing, so its scrollbar is accounted for.
    layoutScroller();
    const to = fullRect();
    if (reducedMotion.matches) {
      setRect(to);
      setChrome(1);
      showStack(idx);
      return;
    }
    zoomAnim = animate(0, 1, {
      ...cfg.zoom.open,
      onUpdate: (p: number) => {
        setRect(lerpRect(from, to, p));
        setChrome(p);
      },
    });
    void zoomAnim.then(() => {
      if (zoomed === idx) showStack(idx);
    });
  }
  function closeZoom(immediate = false) {
    if (zoomed < 0) return;
    zoomAnim?.stop();
    unmagnify(true);
    const idx = zoomed;
    const gridIndex = Math.min(idx, stagePhotos.length - 1);
    if (!single) {
      const [a, b] = rangeAt(stage.scrollLeft);
      if (gridIndex < a || gridIndex > b) scrollToPhoto(gridIndex, false);
    }
    let from: Rect;
    if (!single && zoomScroll.classList.contains('live')) {
      showZoomPhoto(idx);
      const sb = (slots[idx].firstElementChild ?? slots[idx]).getBoundingClientRect();
      from = { x: sb.left, y: sb.top, w: sb.width, h: sb.height };
      setRect(from);
      hideStack();
    } else {
      from = {
        x: 0,
        y: 0,
        w: parseFloat(zoomBox.style.width),
        h: parseFloat(zoomBox.style.height),
      };
      const m = zoomBox.style.transform.match(/translate\(([-\d.]+)px, ([-\d.]+)px\)/);
      if (m) {
        from.x = +m[1];
        from.y = +m[2];
      }
    }
    // Give the page its scrollbar back before measuring where the photo lands, so the target
    // cell is where it will be once the zoom layer is gone.
    unlockPage();
    const to = single ? rectOf(single.origin) : cellRect(gridIndex);
    const finishClose = () => {
      zoom.classList.remove('on');
      zoom.classList.remove('single');
      single = null;
      zoomed = -1;
      restoreFocus();
    };
    if (reducedMotion.matches || immediate) {
      finishClose();
      return;
    }
    zoomAnim = animate(1, 0, {
      ...cfg.zoom.close,
      onUpdate: (p: number) => {
        setRect(lerpRect(to, from, p));
        setChrome(p);
      },
    });
    void zoomAnim.then(() => {
      finishClose();
    });
  }
  function rectOf(node: Element): Rect {
    const b = node.getBoundingClientRect();
    return { x: b.left, y: b.top, w: b.width, h: b.height };
  }
  /** Zoom a photo that is not part of the grid (a floor plan): no counter, no photo stack. */
  function openSingle(photo: { src: string; alt: string }, origin: HTMLElement) {
    if (zoomed >= 0) return;
    zoomAnim?.stop();
    const node = origin.querySelector('img') ?? origin;
    const from = rectOf(node);
    const ratio =
      node instanceof HTMLImageElement && node.naturalWidth > 0 && node.naturalHeight > 0
        ? node.naturalWidth / node.naturalHeight
        : from.w / Math.max(1, from.h);
    single = { origin: node, ratio };
    // Past the last grid photo: "a photo is open", without pointing at a cell.
    zoomed = ALL_PHOTOS.length;
    zoomImg.src = photo.src;
    zoomImg.alt = photo.alt;
    zoom.classList.add('single');
    applyZoomStyle();
    hideStack();
    setRect(from);
    setChrome(0);
    zoom.classList.add('on');
    lockPage();
    focusZoom(origin);
    const to = fullRect();
    if (reducedMotion.matches) {
      setRect(to);
      setChrome(1);
      return;
    }
    zoomAnim = animate(0, 1, {
      ...cfg.zoom.open,
      onUpdate: (p: number) => {
        setRect(lerpRect(from, to, p));
        setChrome(p);
      },
    });
  }
  function zoomStep(dir: number) {
    if (single) return;
    const n = zoomed + dir;
    if (n < 0 || n >= ALL_PHOTOS.length) return;
    showZoomPhoto(n);
    if (zoomScroll.classList.contains('live')) scrollToSlot(n, true);
  }
  const onSlotClick = (e: MouseEvent) => {
    if (!(e.target instanceof Element) || !e.target.closest('.zoom-slot')) return;
    if (panMoved) return;
    const frame = frameOf(e.target);
    if (frame) {
      // On the photo: toggle a closer look. On the dark area around it: close.
      if (magnified) unmagnify();
      else magnifyAt(frame, e.clientX, e.clientY);
      return;
    }
    closeZoom();
  };
  zoomScroll.addEventListener('click', onSlotClick);

  const WHEEL_IDLE_MS = 120;
  let wheel: { v: number; lastT: number } | null = null;
  let wheelT: ReturnType<typeof setTimeout> | undefined;
  const onWheel = (e: WheelEvent) => {
    if (zoomed >= 0) return;
    const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey;
    if (!horizontal) return;
    e.preventDefault();
    let d = e.shiftKey && Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    if (e.deltaMode === 1) d *= 16;
    else if (e.deltaMode === 2) d *= stage.clientWidth;
    const now = performance.now();
    if (!wheel) {
      if (drag) return;
      stopAnim();
      navTarget = null;
      beginLive();
      wheel = { v: 0, lastT: now };
    }
    const dt = Math.max(1, now - wheel.lastT);
    wheel.v = 0.75 * wheel.v + 0.25 * (d / dt);
    wheel.lastT = now;
    stage.scrollLeft = Math.max(0, Math.min(maxScroll(), stage.scrollLeft + d));
    syncScrubber();
    clearTimeout(wheelT);
    wheelT = setTimeout(() => {
      const w = wheel;
      wheel = null;
      if (w) settleSnap({ v: w.v, gain: 1 });
    }, WHEEL_IDLE_MS);
  };
  stage.addEventListener('wheel', onWheel, { passive: false });
  const cellIndexOf = (target: EventTarget | null) => {
    const cell = target instanceof Element ? target.closest('.cell') : null;
    return cell ? cells.findIndex((c) => c.el === cell) : -1;
  };
  const onCellClick = (e: MouseEvent) => {
    if (suppressCellClick) return;
    const idx = cellIndexOf(e.target);
    if (idx >= 0) openZoom(idx);
  };
  track.addEventListener('click', onCellClick);
  const onShowAll = () => openZoom(0);
  showAllButton?.addEventListener('click', onShowAll);
  // A click listener receives the event. closeZoom treats a truthy argument as
  // "skip the animation", so the event must not be forwarded.
  const closeZoomFromClick = () => {
    closeZoom();
  };
  zoomBackdrop.addEventListener('click', closeZoomFromClick);
  zoomBox.addEventListener('click', closeZoomFromClick);
  const PINCH_MIN = 2;
  const onPinch = (e: WheelEvent) => {
    if (!e.ctrlKey) return;
    if (zoomed >= 0) {
      e.preventDefault();
      if (e.deltaY > PINCH_MIN) closeZoom();
    } else if (e.deltaY < -PINCH_MIN) {
      const idx = cellIndexOf(e.target);
      if (idx < 0) return;
      e.preventDefault();
      openZoom(idx);
    }
  };
  zoom.addEventListener('wheel', onPinch, { passive: false });
  stage.addEventListener('wheel', onPinch, { passive: false });
  const onWinResize = () => {
    if (zoomed < 0) return;
    zoomAnim?.stop();
    applyZoomStyle();
    setRect(fullRect());
    setChrome(1);
    if (zoomScroll.classList.contains('live')) {
      layoutScroller();
      scrollToSlot(zoomed, false);
    }
  };
  window.addEventListener('resize', onWinResize);

  const onResize = () => {
    if (stagePhotos.length === 0) return;
    const anchor = visibleRange()[0];
    stopAnim();
    stopBlend();
    liveMode = false;
    fromAv = null;
    fromRange = null;
    const phone = isPhone(root.clientWidth);
    if (phone !== stageIsPhone) {
      clearMobileScrubTimer();
      mobileScrub = null;
      setMobileScrubPresentation(false);
      stageIsPhone = phone;
      stagePhotos = stageIsPhone ? ALL_PHOTOS : GRID_PHOTOS;
      buildStage();
      buildThumbs();
    } else {
      sizeColumns();
      sizeThumbs();
    }
    sizeMobileIndicator();
    scrollToPhoto(Math.min(anchor, stagePhotos.length - 1), false);
  };
  function applyFrame() {
    const g = geometry(root.clientWidth, cfg.grid);
    root.style.height = g.frameH + 'px';
    root.style.setProperty('--pad', cfg.grid.padding + 'px');
    root.style.setProperty('--gap', cfg.grid.gap + 'px');
    root.style.setProperty('--stage-radius', cfg.grid.topRadius + 'px');
    root.style.setProperty('--hero-w', g.heroW + 'px');
    root.style.setProperty('--hero-h', g.heroH + 'px');
  }
  applyFrame();
  applyZoomStyle();
  let lastW = root.clientWidth;
  const ro = new ResizeObserver(() => {
    if (root.clientWidth === lastW) return;
    lastW = root.clientWidth;
    applyFrame();
    onResize();
  });

  buildStage();
  buildThumbs();
  sizeMobileIndicator();
  // The grid now shows the hero itself.
  const ssrHero = root.querySelector('.ssr-hero');
  if (ssrHero instanceof HTMLElement) ssrHero.hidden = true;
  rTo = rangeAt(0);
  blend = 1;
  syncScrubber();
  ro.observe(root);

  return {
    closeZoom,
    openPhoto: openSingle,
    destroy: () => {
      unlockPage();
      ro.disconnect();
      clearMobileScrubTimer();
      mobileScrub = null;
      setMobileScrubPresentation(false);
      stage.removeEventListener('touchmove', onStageTouchMove);
      stopAnim();
      stopBlend();
      clearTimeout(wheelT);
      zoomAnim?.stop();
      document.body.style.cursor = '';
      if (!inert) window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onWinResize);
      zoomScrubber.removeEventListener('click', onZoomScrubberClick);
      showAllButton?.removeEventListener('click', onShowAll);
      zoom.classList.remove('on');
    },
  };
}

type TourAction = NonNullable<ReturnType<typeof useTouring>>['primaryAction'];

const ZoomBarButton = ({
  action,
  label = action.text,
  primary,
}: {
  action: TourAction;
  label?: string;
  primary: boolean;
}) => {
  return (
    <Button
      analyticsName={action.analyticsName}
      data-analytics-name={action.analyticsName}
      aria-label={label}
      variant={primary ? 'primary' : 'secondary'}
      size="sm"
      onClick={action.onClick}
      disabled={action.disabled}
      href={action.disabled ? undefined : action.href}
      target={action.target}
      rel="noopener"
      className="whitespace-nowrap rounded-xl px-5 text-label-sm"
    >
      {label}
    </Button>
  );
};

/** The self-tour / buy buttons for the zoom bar, mirroring the price card's choices. */
const ZoomBarActions = ({
  property,
  onDialogActive,
}: {
  property: PropertyDetailsType;
  onDialogActive: (active: boolean) => void;
}) => {
  const touring = useTouring(property, { placement: 'lightbox' });
  const dialogActive = Boolean(
    touring &&
    (touring.isDialogOpen || touring.isSelfTourDialogOpen || touring.isBuyDirectContextOpen),
  );
  useEffect(() => {
    onDialogActive(dialogActive);
    return () => onDialogActive(false);
  }, [dialogActive, onDialogActive]);
  if (!touring) return null;
  const tourIsSelf = touring.tourKind === 'selfTour';
  const leftIsPrimary =
    touring.tourKind === 'selfTour' || touring.secondaryAction?.variant === 'primary';
  const rightIsPrimary = !touring.showBuyDirect && touring.primaryAction.variant !== 'secondary';
  return (
    <>
      <div className="zoom-bar-actions flex items-center gap-3">
        {touring.secondaryAction && (
          <ZoomBarButton
            action={touring.secondaryAction}
            label={tourIsSelf ? 'Take a self tour' : undefined}
            primary={leftIsPrimary}
          />
        )}
        <ZoomBarButton
          action={touring.primaryAction}
          label={!touring.showBuyDirect && tourIsSelf ? 'Take a self tour' : undefined}
          primary={rightIsPrimary}
        />
      </div>
      {touring.tourKind === 'tourWithAgent' && (
        <TourWithAgentModal
          property={property}
          isOpen={touring.isDialogOpen}
          onClose={touring.closeDialog}
        />
      )}
      <SelfTourDialog
        isOpen={touring.isSelfTourDialogOpen}
        onClose={touring.closeSelfTourDialog}
        onOpenRequest={touring.openSelfTourDialog}
        placement="lightbox"
        addressToken={property.addressToken}
        slug={property.slug}
        tourBookingListingId={property.tourBookingListingId}
        addressLine={[property.street, property.city].filter(Boolean).join(', ')}
      />
      <BuyDirectContextModal
        isOpen={touring.isBuyDirectContextOpen}
        onClose={touring.closeBuyDirectContext}
        continueHref={touring.buyDirectContextHref}
      />
    </>
  );
};

const VIEWER_SRCSET_PRESETS = [
  ['3:2-sm', 768],
  ['3:2-md', 1200],
  ['3:2-lg', 1440],
  ['3:2-xl', 1800],
] as const;

const viewerPhotoSrcSet = (photo: GalleryPhoto): string | undefined => {
  const source = toImagesWorkerSourceUrl(photo.url);
  if (!source) return undefined;
  return VIEWER_SRCSET_PRESETS.map(
    ([preset, width]) =>
      `${buildWorkerPhotoUrl(source, { preset, presetScale: '1', dpr: '1' })} ${width}w`,
  ).join(', ');
};

const viewerPhotoSrc = (photo: GalleryPhoto): string => {
  const source = toImagesWorkerSourceUrl(photo.url);
  if (source) return buildWorkerPhotoUrl(source, { preset: '3:2-xl', presetScale: '1', dpr: '1' });
  return pickFirstPresizedUrl(photo, ['xl', 'lg', 'md']) ?? photo.url;
};

const isWideViewerPhoto = (index: number): boolean =>
  index === 0 || (index >= 5 && (index - 5) % 3 === 0);

/** Listing photos only: floor plans keep their own section and open in the lightbox. */
const buildViewerPhotos = (property: PropertyDetailsType): ViewerPhoto[] =>
  buildGalleryPhotos(property)
    .filter((photo) => !photo.isFloorPlan)
    .map((photo, index) => ({
      // One ~1800px 3:2 render shared by the cell, the zoom morph and the zoom stack.
      src: viewerPhotoSrc(photo),
      srcSet: viewerPhotoSrcSet(photo),
      thumb: getLightboxThumbnailUrl(photo),
      alt: `Photo ${index + 1} of ${property.street ?? 'this home'}`,
      wide: isWideViewerPhoto(index),
    }));

type PdpPhotoViewerProps = {
  property: PropertyDetailsType;
  onCopyLink: () => void;
  heroOverlay?: ReactNode;
  showTour3d?: boolean;
};

/** What the page can ask the viewer to do from outside, e.g. zoom a floor plan. */
export type PdpPhotoViewerHandle = {
  openPhoto: (photo: { src: string; alt: string }, origin: HTMLElement) => void;
};

export const PdpPhotoViewer = forwardRef<PdpPhotoViewerHandle, PdpPhotoViewerProps>(
  function PdpPhotoViewer({ property, onCopyLink, heroOverlay, showTour3d }, ref) {
    const rootRef = useRef<HTMLDivElement>(null);
    const zoomRef = useRef<HTMLDivElement>(null);
    const onZoomDialogActive = useCallback((active: boolean) => {
      zoomRef.current?.classList.toggle('yield', active);
    }, []);
    const controlsRef = useRef<ReturnType<typeof mountViewer> | null>(null);
    // The zoom layer portals into document.body, so it only renders once we are on the client.
    const mounted = useSyncExternalStore(
      () => () => {},
      () => true,
      () => false,
    );
    // The viewer engine mounts once; it reads the latest handlers through this ref.
    const callbacks = useRef({ onCopyLink, onLike: property.onToggleFavorite });
    useEffect(() => {
      callbacks.current = { onCopyLink, onLike: property.onToggleFavorite };
    });
    // Built once per mounted viewer (the parent remounts it per home via `key`).
    const [{ photos, gridPhotos }] = useState(() => {
      const allPhotos = buildViewerPhotos(property);
      return { photos: allPhotos, gridPhotos: allPhotos.slice(0, 5) };
    });

    useEffect(() => {
      const root = rootRef.current;
      const zoom = zoomRef.current;
      if (!mounted || !root || !zoom) return undefined;
      const controls = mountViewer(root, zoom, gridPhotos, photos, CONFIG);
      controlsRef.current = controls;
      return () => {
        controlsRef.current = null;
        controls.destroy();
      };
    }, [gridPhotos, mounted, photos]);
    useImperativeHandle(
      ref,
      () => ({ openPhoto: (photo, origin) => controlsRef.current?.openPhoto(photo, origin) }),
      [],
    );

    const address = formatStreetAddress(property.street, property.unitType, property.unitNumber);
    const tour3dUrl = property.tour3d?.url;
    const locality = [[property.city, property.state].filter(Boolean).join(', '), property.zip]
      .filter(Boolean)
      .join(' ');
    const emptyLocation =
      photos.length === 0
        ? streetViewLocationFor({
            availableSoon: property.listingState === 'AVAILABLE_SOON',
            photoCount: 0,
            latitude: property.latitude,
            longitude: property.longitude,
          })
        : null;

    const zoomLayer = (
      <div
        ref={zoomRef}
        className="pv-zoom"
        aria-label="Home photos"
        aria-modal="true"
        role="dialog"
        tabIndex={-1}
      >
        <div className="zoom-backdrop" data-pv="zoomBackdrop" />
        <div className="zoom-bar" data-pv="zoomBar">
          <div className="zoom-bar-address">
            <span className="text-body-sm text-od-content-primary">{address}</span>
            <span className="text-body-sm text-od-content-secondary">{locality}</span>
          </div>
          <div className="zoom-bar-right">
            <ZoomBarActions property={property} onDialogActive={onZoomDialogActive} />
            <div className="zoom-bar-icons">
              <button
                type="button"
                className="icon-btn"
                aria-label="Copy home link"
                onClick={() => {
                  controlsRef.current?.closeZoom(true);
                  callbacks.current.onCopyLink();
                }}
              >
                <IconShare2 size={24} stroke={2} />
              </button>
              <button
                type="button"
                className="icon-btn favorite group/favorite"
                aria-label={property.isLiked ? 'unfavorite' : 'favorite'}
                aria-pressed={property.isLiked}
                onClick={() => callbacks.current.onLike()}
              >
                <FavoriteHeartIcon filled={property.isLiked} interactive size={24} />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Close photos"
                onClick={() => controlsRef.current?.closeZoom()}
              >
                <IconX size={24} stroke={2} />
              </button>
            </div>
          </div>
        </div>
        <div className="zoom-img" data-pv="zoomImg">
          <img alt="" draggable="false" />
        </div>
        <div className="zoom-scroll" data-pv="zoomScroll" />
        <div className="counter" data-pv="zoomCounter">
          1 of {gridPhotos.length}
        </div>
        <div
          className="zoom-scrubber"
          data-pv="zoomScrubber"
          role="navigation"
          aria-label="Lightbox photo scrubber"
        />
      </div>
    );

    let stageHero: ReactNode;
    if (photos.length > 0) {
      const hero = photos[0];
      stageHero = (
        <img
          className="ssr-hero"
          src={hero.src}
          srcSet={hero.srcSet}
          sizes={hero.srcSet ? VIEWER_IMAGE_SIZES : undefined}
          alt={hero.alt}
          fetchPriority="high"
          decoding="async"
          draggable="false"
        />
      );
    } else if (emptyLocation) {
      stageHero = (
        <div className="empty-hero">
          <StreetViewPhoto
            latitude={emptyLocation.latitude}
            longitude={emptyLocation.longitude}
            alt={streetViewAltText(property)}
            fallbackLabel="Photos coming soon"
          />
        </div>
      );
    } else {
      stageHero = (
        <div className="empty-hero">
          <PropertyPhotoPlaceholder
            state="unavailable"
            label={property.listingState === 'AVAILABLE_SOON' ? 'Photos coming soon' : undefined}
          />
        </div>
      );
    }

    return (
      <div ref={rootRef} className={photos.length === 0 ? 'pv-root empty' : 'pv-root'}>
        {/* Inserted as raw text so server and browser produce the identical style node. */}
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        {mounted && createPortal(zoomLayer, document.body)}
        <div className="viewer" data-pv="viewer">
          <div className="counter" data-pv="counter">
            1 of {photos.length}
          </div>
          <div className="stage" data-pv="stage">
            {/* Visible before hydration (and without JavaScript); the engine hides it once the grid is built. */}
            {stageHero}
            <div className="track" data-pv="track" />
            {heroOverlay && (
              <div className="hero-overlay" data-pv="heroOverlay">
                {heroOverlay}
              </div>
            )}
            {showTour3d && tour3dUrl && (
              <div className="tour-3d" data-pv="tour3d">
                <GalleryMediaActions tour3dUrl={tour3dUrl} addressLabel={address} />
              </div>
            )}
            {photos.length > 0 && (
              <Button
                type="button"
                variant="secondary"
                size="md"
                analyticsName="pdp-refresh-show-all-photos"
                aria-label={`Show all ${photos.length} photos`}
                className="gallery-action-button show-all-photos"
                data-pv="showAll"
              >
                <IconLibraryPhoto size={18} stroke={2} aria-hidden="true" />
                <span>Show all {photos.length} photos</span>
              </Button>
            )}
          </div>
          <div className="scrubber" data-pv="scrubber">
            <div className="strip-wrap">
              <div className="pill" data-pv="pill">
                <div className="clip" data-pv="clip">
                  <div className="rail" data-pv="rail">
                    <div className="window" data-pv="window" />
                    <div className="thumbs" data-pv="thumbs" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={
              photos.length === 1 ? 'mobile-page-indicator single' : 'mobile-page-indicator'
            }
            data-pv="mobileIndicator"
            aria-hidden="true"
          >
            {photos.map((photo, index) => (
              <span
                key={photo.src}
                className={index === 0 ? 'mobile-page-segment active' : 'mobile-page-segment'}
              />
            ))}
          </div>
        </div>
      </div>
    );
  },
);
